import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCrown, FaMoneyBillWave } from "react-icons/fa";
import AccountExportOptions from "../../AccountExportOptions";
import DynamicSelect from "../../DynamicSelect";
import { useAuth } from "../../../utils/AuthContext";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const HANDOVER_API = "https://api.muktihospital.com/api/pathology-handover";
const MD_SIR = "MD Sir";

const PathologyMdAccount = () => {
  const { user } = useAuth();
  const today = new Date().toISOString().split("T")[0];
  const [filterType, setFilterType] = useState("daily");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const [ownerName, setOwnerName] = useState(null);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Numbers the requests so only the newest one may touch the screen. Without
  // it a slow reply for an earlier filter (e.g. the Daily load on page open)
  // can land after the one for the filter now chosen and show its figures
  // under the wrong period.
  const latestRequest = useRef(0);

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
    { value: "all", label: "All Time" },
  ];

  const getFilterSubtitle = () => {
    let dateText = "All Time";
    if (filterType === "daily") {
      const fStart = new Date(startDate).toLocaleDateString("en-GB");
      const fEnd = new Date(endDate).toLocaleDateString("en-GB");
      dateText = fStart === fEnd ? fStart : `${fStart} - ${fEnd}`;
    } else if (filterType === "monthly") {
      const [year, month] = selectedMonth.split("-");
      const dateObj = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
      dateText = dateObj.toLocaleString("en-US", { month: "long", year: "numeric" });
    } else if (filterType === "yearly") {
      dateText = `Year ${selectedYear}`;
    }
    return `MD Sir: ${ownerName || "N/A"}  |  Period: ${dateText}`;
  };

  const exportHeaders = ["SL", "Date", "Sent By", "Note", "Amount"];
  const exportDataMapper = (row) => {
    const index = records.findIndex((r) => r.id === row.id);
    return [
      index + 1,
      row.handoverDate ? new Date(row.handoverDate).toLocaleDateString("en-GB") : "N/A",
      row.fromUser || "N/A",
      row.note || "-",
      (Number(row.amount) || 0).toFixed(2),
    ].join("\t");
  };

  const summaryFooter = useMemo(() => [
    [
      { content: "TOTAL", colSpan: 4, styles: { fontStyle: "bold", halign: "left" } },
      { content: Number(total).toFixed(2), styles: { fontStyle: "bold", halign: "left" } },
    ]
  ], [total]);

  const fetchMdAccount = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const params = { type: filterType };

      if (filterType === "daily") {
        params.startDate = startDate;
        params.endDate = endDate;
      } else if (filterType === "monthly") {
        const [y, m] = selectedMonth.split("-");
        params.year = y;
        params.month = m;
      } else if (filterType === "yearly") {
        params.year = selectedYear;
      }

      // /md-account looks the owner up by role, and no user has role "owner",
      // so it returns nothing. Every handover to MD Sir carries toUser "MD Sir"
      // (the desktop app's name for the owner), so read those directly.
      params.toUser = MD_SIR;
      const res = await axios.get(`${HANDOVER_API}/list`, {
        headers: { "x-api-key": API_KEY },
        params,
      });

      if (requestId !== latestRequest.current) return;
      const rows = res.data.records || [];
      setOwnerName(MD_SIR);
      setTotal(rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0));
      setCount(rows.length);
      setRecords(rows);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      console.error("MD account error:", err);
      toast.error("Failed to load MD account.");
      // Do not leave the previous period's figures under the new filter.
      setTotal(0);
      setCount(0);
      setRecords([]);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [filterType, startDate, endDate, selectedMonth, selectedYear]);

  useEffect(() => {
    fetchMdAccount();
  }, [fetchMdAccount]);

  return (
    <div>
      <ToastContainer position="top-right" autoClose={2500} />
      <div className="bg-white shadow-sm rounded-lg">
        {/* Header with Filters */}
        <div className="border-b border-dashed border-M-text-color/50 p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              <FaCrown className="text-amber-500" /> MD Sir Account
              {ownerName && <span className="text-sm text-gray-400 font-normal">({ownerName})</span>}
            </h2>
            <p className="text-xs text-gray-400 mt-1">Total cash handed over to the owner from daily closing.</p>
          </div>

          {/* Filter Bar */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm font-bold text-gray-600">Filter:</label>
              <div className="w-28">
                <DynamicSelect
                  options={filterOptions}
                  value={filterOptions.find((o) => o.value === filterType)}
                  onChange={(o) => setFilterType(o.value)}
                  isSearchable={false}
                />
              </div>
            </div>

            {/* Daily Date Range */}
            {filterType === "daily" && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border rounded-md p-1.5 text-sm outline-none"
                />
                <span className="text-gray-400">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border rounded-md p-1.5 text-sm outline-none"
                />
              </div>
            )}

            {/* Monthly Picker */}
            {filterType === "monthly" && (
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border rounded-md p-1.5 text-sm outline-none"
              />
            )}

            {/* Yearly Picker */}
            {filterType === "yearly" && (
              <input
                type="number"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="border rounded-md p-1.5 text-sm outline-none w-24"
                min="2000"
                max="2100"
              />
            )}

            {records.length > 0 && (
              <AccountExportOptions
                data={records}
                title={`MD Sir Account - ${ownerName || ""}`}
                subTitle={getFilterSubtitle()}
                headers={exportHeaders}
                dataMapper={exportDataMapper}
                footer={summaryFooter}
                printedBy={user?.name || "Accounts"}
                dropdownOpen={dropdownOpen}
                toggleDropdown={() => setDropdownOpen(!dropdownOpen)}
              />
            )}
          </div>
        </div>

        {/* Total card */}
        <div className="p-4 border-b border-gray-100">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-amber-500 text-white p-3 rounded-full">
                <FaMoneyBillWave size={20} />
              </div>
              <div>
                <p className="text-[11px] font-black uppercase text-amber-700">Total Received by MD Sir</p>
                <p className="text-xs text-gray-500">{count} handover{count !== 1 ? "s" : ""} in this period</p>
              </div>
            </div>
            <p className="text-3xl font-black text-amber-700">৳{Number(total).toFixed(2)}</p>
          </div>
        </div>

        {/* History table */}
        <div className="overflow-x-auto p-4">
          {!ownerName ? (
            <div className="text-center py-10 text-red-400 text-sm">
              No owner set. Please set a user's role to <b>owner</b> in the database.
            </div>
          ) : (
            <table className="min-w-[600px] w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100 text-left text-[11px] uppercase font-medium text-M-heading-color">
                  <th className="p-3">Date</th>
                  <th className="p-3">Sent By (From)</th>
                  <th className="p-3">Note</th>
                  <th className="p-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} className="text-center py-8 text-gray-400 text-sm">Loading...</td></tr>
                ) : records.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-10 text-gray-400 text-sm">No handovers to MD Sir in this period.</td></tr>
                ) : (
                  records.map((h) => (
                    <tr key={h.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-sm text-gray-600">{new Date(h.handoverDate).toLocaleDateString("en-GB")}</td>
                      <td className="p-3 text-sm font-semibold text-gray-800 capitalize">{h.fromUser?.toLowerCase()}</td>
                      <td className="p-3 text-sm text-gray-500">{h.note || "—"}</td>
                      <td className="p-3 text-sm font-black text-emerald-600 text-right">৳{Number(h.amount).toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PathologyMdAccount;